import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  NgZone,
  ViewChild,
} from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  Observable,
  of,
  take,
  tap,
  shareReplay,
  Subscription,
  switchMap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash.clonedeep';

import {
  GridApi,
  GridColumn,
  GridOptions,
  GridSync,
  SortDescriptor,
  GridStackedHeaderRow,
  anyFnExec,
  Group,
  GroupConfig,
  IIdentity,
  FilterColumn,
  FilterControls,
} from './models';
import { DefaultDialogConfig, Constants } from './utils/constants';
import { Changes, ChangesModel } from './collections/changes-model';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { objectEquals } from './utils/object';
import { SelectionModel } from './collections/selection-model';
import {
  GridFooterType,
  GridSortDirection,
  GridSortMode,
  GridAlignment,
  GridCellContentAlignment,
} from './enums';
import {
  FocusManagerService,
  EditService,
  RefreshService,
  DataFilterService,
} from './services';
import { TooltipMessageComponent } from './components/tooltip-message/tooltip-message.component';
import { ColorService } from './services/color.service';
import { FilterSelectEditComponent } from './components';

/* eslint-disable */
@UntilDestroy()
@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // the edit service must be provided at grid level as we don't want
  // instances to be shared between grids due to issues that can appear when there
  // are more than one grid rendered on the same screen
  // the data filter service must be provided for the case that multiple grids
  //  rendered on the same page use filters
  providers: [EditService, DataFilterService],
})
export class GridComponent<T extends IIdentity>
  implements OnChanges, AfterViewInit
{
  //#region Inputs

  @Input({ required: true }) data!: T[];
  @Input({ required: true }) options!: GridOptions<T>;
  @Input() columns: GridColumn<T>[] = [];
  @Input() stackedHeaderRows!: GridStackedHeaderRow[];
  @Input() filterControls!: FilterControls<T>;

  //#endregion

  //#region Templates

  @ViewChild(MatTable) matTable!: MatTable<T | Group>;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.dataSource.sort = ms;
  }

  //#endregion

  //#region Bindings

  @HostBinding('style.--mat-column-select-width')
  get matColumnSelectWidth(): string {
    return `${this.defaultColumns.select.definition.width ?? 30}px`;
  }

  @HostBinding('style.--mat-column-sequence-width')
  get matColumnSequenceWidth(): string {
    return `${this.defaultColumns.sequence.definition.width ?? 30}px`;
  }

  @HostBinding('style.--mat-column-drag-and-drop-width')
  get matColumnDragAndDrop(): string {
    return `${this.defaultColumns.dragAndDrop.definition.width ?? 30}px`;
  }

  @HostBinding('style.--mat-column-row-group-width')
  get matColumnRowGroup(): string {
    return `${this.defaultColumns.rowGroup.definition.width ?? 200}px`;
  }

  @HostBinding('style.--mat-row-height')
  get matRowHeight(): string {
    return `${this.options?.rowConfig?.height ?? 49}px`;
  }

  @HostBinding('style.--pageHeader-height')
  get pageHeaderHeight(): string {
    return '50px';
  }

  @HostBinding('style.--total-header-height')
  get totalHeaderHeight(): string {
    return `${
      44 *
        ((this.stackedHeaderRows?.length ?? 0) +
          (!!this.filterControls?.columns ? 1 : 0) +
          1) +
      1
    }px`;
  }

  //#endregion

  displayedColumns: string[] = [];
  filteredColumns: string[] = [];
  dataSource = new MatTableDataSource<T | Group>();
  // TODO(Grid) this should not be part of grid (extract in app default config)
  defaultRootMargin = `-${this.pageHeaderHeight} 0px 0px 0px`;
  dragDisabled = true;
  entityLabel = this.options?.labeling?.entityLabel
    ? `qa-grid-${this.options.labeling.entityLabel}`
    : '';
  sortingDefinition!: SortDescriptor<T>[];

  groupByColumn: string;
  groups: Group[];
  collapsedGroups: number[] = [];
  rowGroupsCellSpans: number[] = [];

  rendering$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  readonly changes = new ChangesModel<T>();
  readonly selection = new SelectionModel<T>();
  // Bumped whenever the selectable rows are refreshed, every full repaint
  // included, so the pure pipes on the row checkbox and on each cell's tab stop,
  // focus classes and tooltip re-run a predicate whose inputs changed but whose
  // reference did not.
  rowsRevision = 0;
  readonly shouldRecomputeFooter$ = new BehaviorSubject<{
    column: string;
    timestamp: Date;
  }>(null);

  /**
   * @description Programmatic interaction with the grid should be made through this api, not by referencing the grid component itself.
   * This api is exposed through onGridRegistered event in the grid options object.
   */
  readonly api: GridApi<T> = {
    addRows: this.addRows.bind(this),
    insertRows: this.insertRows.bind(this),
    updateRow: this.updateRow.bind(this),
    deleteRows: this.deleteRows.bind(this),
    editCell: (data: T, columnDef: string, value: any) => {
      if (!data || !columnDef) {
        throw new Error(
          'Argument null exception. Please provide row and column'
        );
      }

      const oldValue = data[columnDef as keyof T];
      data[columnDef as keyof T] = value;

      this.onCellEdited(data, columnDef, null, oldValue);
    },
    getChanges: this.getChanges.bind(this),
    getChangesWithUpdatedPriorities:
      this.getChangesWithUpdatedPriorities.bind(this),
    isDirty: this.isDirty.bind(this),
    isDirty$: this.changes.isDirty$,
    hasChanges: this.changes.hasValue.bind(this.changes),
    hasChanges$: this.changes.hasValue$.asObservable(),
    setPredefinedChanges: this.setPredefinedChanges.bind(this),
    removeChanges: this.removeChanges.bind(this),
    isPropertyChanged: this.isPropertyChanged.bind(this),
    getSelection: this.getSelections.bind(this),
    selected$: this.selection.selectedValues$.asObservable(),
    save: this.onSave.bind(this),
    reset: this.reset.bind(this),
    selectAll: this.selectAll.bind(this),
    refreshSorting: this.refreshSorting.bind(this),
    setSortingDefinition: this.setSortingDefinition.bind(this),
    scrollTo: this.scrollTo.bind(this),
    scrollToLastRow: this.scrollToLastRow.bind(this),
    openEdit: this.openEdit.bind(this),
    repaintRows: this.repaintRows.bind(this),
    repaintRow: this.repaintRow.bind(this),
    refreshSelectableRows: this.refreshSelectableRows.bind(this),
    refreshFilters: this.refreshFilters.bind(this),
    rendering$: this.rendering$,
    setAllowAdding: this.setAllowAdding.bind(this),
  };
  readonly defaultColumns = {
    dragAndDrop: {
      definition: {
        columnDef: 'dragAndDrop',
        width: this.options?.dragAndDropConfig?.columnWidth ?? 50,
      },
      enabled: () => this.options?.dragAndDropConfig?.allowDragAndDrop,
    },
    select: {
      definition: {
        columnDef: 'select',
        width: this.options?.selectionConfig?.columnWidth ?? 30,
      },
      enabled: () => this.options?.selectionConfig?.allowSelection,
    },
    rowGroup: {
      definition: {
        columnDef: 'rowGroup',
        width: this.options?.rowGroupConfig?.columnWidth ?? 200,
      },
      enabled: () => this.options?.rowGroupConfig?.enableRowGrouping,
    },
    sequence: {
      definition: {
        columnDef: 'sequence',
        width: this.options?.sequenceConfig.columnWidth ?? 30,
      },
      enabled: () => !this.options?.sequenceConfig?.disabled,
    },
  };

  GridSortDirection = GridSortDirection;
  GridSortMode = GridSortMode;
  GridAlignment = GridAlignment;
  GridFooterType = GridFooterType;
  TooltipMessageComponent = TooltipMessageComponent;

  private dataCellColors: Map<number | string, string>;
  private filterSubscription: Subscription;

  //#region Listeners

  @HostListener('window:keydown.shift.+', ['$event'])
  onAddRowShortcut(): void {
    if (
      this.options?.addConfig?.allowAdding &&
      !this.options?.addConfig?.disableAddShortcut &&
      !!this.element.nativeElement.offsetParent &&
      (!this.dialog.openDialogs.length || this.isCurrentGridOpenedInTopDialog())
    ) {
      this.onAddRow();
    }
  }

  //#endregion

  constructor(
    private focusMonitor: FocusMonitor,
    private focusManagerService: FocusManagerService,
    private refreshService: RefreshService,
    private editService: EditService,
    private colorService: ColorService,
    private dialog: MatDialog,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private element: ElementRef,
    private dataFilterService: DataFilterService
  ) {
    // Refresh the grid sorting when user clicks outside of the grid.
    // This will prevent situations when user edits a column that is part of the sorting process
    // and then when he clicks on another cell on the same row, the rows moves up or down according to the sorting
    this.focusManagerService.focused
      .pipe(
        untilDestroyed(this),
        debounceTime(50),
        filter((x) => x == null)
      )
      .subscribe(() => {
        this.refreshSorting();
      });

    this.focusManagerService.isCurrentWindowBlurred$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // if the current window is blurred reset the current active element (default body)
        // so that cell does not enter edit mode when switching back to the current tab
        (document.activeElement as HTMLElement)?.blur();
      });

    this.refreshService.updateWidthForColumn$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!this.defaultColumns[x.columnDef])
      )
      .subscribe((x) => {
        this.defaultColumns[x.columnDef].definition.width = x.updatedWidth;
      });
  }

  //#region Lifecycle hooks

  ngAfterViewInit(): void {
    this.options?.events?.onGridRegistered?.(this.api);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data?.currentValue) {
      this.refreshDataSource(true);
    }

    if (changes.options?.currentValue) {
      if (this.options.sortConfig) {
        this.setSortingDefinition(this.options.sortConfig.definition);
      }
    }

    if (changes.options || changes.columns) {
      this.computeDisplayedColumns();

      if (this.options.groupConfig) {
        this.computeGroups();
      }

      if (changes.filterControls) {
        this.initFilters();
      }

      // make sure there are not any cells opened in edit mode
      // for changes in options/columns be reflected
      this.editService.closeEditAll();
    }

    if (changes.options) {
      this.recomputeDefaultColumnsWidths();
      this.refreshService.recomputeDynamicWidth$.next('sequence');
    }

    if (changes.data?.currentValue || changes.options?.currentValue) {
      this.refreshSorting();
      this.selection.sync(this.filterSelectableRows(this.data));

      if (this.data?.length && this.options.rowGroupConfig?.enableRowGrouping) {
        this.computeRowGroupsCellSpans();
      }
    }

    if (this.data?.length && this.options.colorConfig?.allowColorPicker) {
      this.computeCellsColor();
    }

    // We must wait for sorting
    if (changes.data?.currentValue) {
      this.options?.events?.onDataReady?.();
    }
  }

  //#endregion

  /**
   * @description Adds a new row to the grid as a result of user clicking the grid add button.
   * The added object is created by computing the options.addConfig.
   * If configured the grid will automatically enter in edit mode the first editable cell
   */
  onAddRow(): void {
    if (this.options?.addConfig?.mode === 'dialog') {
      return;
    }

    const rows = this.addRows(this.rowDefaultValue);

    // timeout is needed to let the DOM create the cells
    setTimeout(() => {
      this.scrollTo(rows[0]);
      this.focusRow(rows[0], this.options?.addConfig?.autoStartEdit ?? true);
    });

    this.options?.addConfig?.onAdded?.(...rows);

    //This is needed in case the select all checkbox has been checked before adding new rows
    //so that newly added rows are checked automatically
    if (this.selection.getIsSelectAllActive()) {
      this.selectAll();
    }
  }

  /**
   *
   * @param columnDef the column that was edited
   * @param columnsDef additional columns that were edited along with the current column
   * @param oldValue the value before the column was edited
   */
  onCellEdited(
    data: T,
    columnDef: string,
    columnsDef?: string[],
    oldValue?: any
  ): void {
    const editedColumns = [columnDef, ...(columnsDef ?? [])];

    this.changes.write({ value: data, properties: editedColumns as any });

    this.notifyChanges(data, editedColumns, oldValue);

    this.refreshRow(data, editedColumns);

    if (this.groupConfig && columnDef === this.groupConfig.groupBy) {
      this.recomputePriority(data);
      this.refreshDataSource(true);
    }

    // timeout is needed to ensure that all related cells are refreshed before refreshing the footer
    setTimeout(() => {
      this.refreshFooter(editedColumns);

      if (this.filterControls) {
        this.filterData();
      }
    });
  }

  onDeleteRow(data: T, cell: HTMLElement): void {
    if (this.isRowEmpty(data)) {
      return this.deleteRows(data);
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      ...DefaultDialogConfig,
      data: {
        title:
          this.options?.deleteConfig?.confirmationTitle ??
          'Delete selected item?',
        message:
          this.options?.deleteConfig?.confirmationMessage ??
          'Are you sure you want to delete the selected item?',
        confirmButtonLabel: this.options?.labeling?.deleteButton ?? 'Delete',
        cancelButtonLabel: this.options?.labeling?.cancelButton,
        icon: 'delete',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result: boolean) => {
        if (result) {
          this.deleteRows(data);
        }

        // Restore focus after closing the dialog
        this.focusMonitor.focusVia(cell, 'mouse');
      });
  }

  onDeleteSelectedRows(): void {
    const dirtyRows = this.selection.selected.filter(
      (x) => !this.isRowEmpty(x)
    );

    if (!dirtyRows?.length) {
      // TODO(Grid) Restore focus to table
      return this.deleteRows(...this.selection.selected);
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      ...DefaultDialogConfig,
      data: {
        title:
          this.options?.deleteConfig?.selectionConfirmationTitle ??
          'Delete selected items?',
        message:
          this.options?.deleteConfig?.selectionConfirmationMessage ??
          'Are you sure you want to delete the selected items?',
        confirmButtonLabel: this.options?.labeling?.deleteButton ?? 'Delete',
        cancelButtonLabel: this.options?.labeling?.cancelButton,
        icon: 'delete',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result: boolean) => {
        if (result) {
          this.deleteRows(...this.selection.selected);
        }

        // TODO(Grid) Restore focus after closing the dialog
      });
  }

  /**
   * @deprecated Use your own saving mechanism for the sake of simplicity.
   * This will be removed from the grid.
   */
  onSave(): Observable<T[]> {
    if (!this.options?.saveConfig) {
      throw new Error('Save config was not provided!');
    }

    const changes = this.getChanges();

    const beforeSave$ =
      this.options?.saveConfig.beforeSave?.(changes) ?? of(true);

    const save$ = beforeSave$.pipe(
      filter((x) => !!x),
      switchMap(() => this.save(changes)),
      shareReplay()
    );

    save$.pipe(untilDestroyed(this)).subscribe();

    return save$;
  }

  onToggleSortDirection(): void {
    const direction = this.selectedSortOption?.direction;

    if (direction === GridSortDirection.Ascending) {
      this.selectedSortOption.direction = GridSortDirection.Descending;
    } else {
      this.selectedSortOption.direction = GridSortDirection.Ascending;
    }

    this.refreshSorting();
  }

  onSortColumnChanged(columnDef: keyof T): void {
    this.setSortingDefinition([{ ...this.selectedSortOption, columnDef }]);

    this.refreshSorting();
  }

  onRowDragged(event: CdkDragDrop<(T | Group)[]>): void {
    this.groupConfig ? this.groupDrag(event) : this.noGroupDrag(event);
  }

  onRowClicked(data: T): void {
    if (this.options?.editConfig?.mode === 'inline') {
      return;
    }

    if (this.options?.editConfig?.mode === 'dialog') {
      this.options?.editConfig?.onRowClick(data);
    }
  }

  trackByColumn(_index: number, column: GridColumn<T>): string {
    return column.columnDef;
  }

  trackByRow(_index: number, row: T | Group): string | number {
    return row.id;
  }

  trackByIndex(_index: number): number {
    return _index;
  }

  // grouping related logic
  onToggleExpandedGroup(group: Group): void {
    const groupValue = group.value as number;

    if (this.collapsedGroups.indexOf(groupValue) > -1) {
      this.collapsedGroups = this.collapsedGroups.filter(
        (x) => x !== groupValue
      );
    } else {
      this.collapsedGroups = [...this.collapsedGroups, groupValue];
    }
  }

  isGroup(_index, item: any): boolean {
    return !!item?.level;
  }

  get selectedSortOption(): SortDescriptor<T> {
    // There can be just one option selected at a time
    return this.sortingDefinition?.[0];
  }

  //#region Private methods

  private initFilters(): void {
    this.dataSource.filterPredicate =
      this.filterControls.customFilterPredicate ??
      this.filterPredicate.bind(this);

    this.filterControls.columns.forEach(
      (x) =>
        (x.columnDef = `${this.filterControls.id}__filterColumn__${x.columnDef}`)
    );

    if (
      !this.filterControls.disableRowGroupFiltering &&
      !!this.options.rowGroupConfig
    ) {
      this.filterControls.columns.push(this.filterRowGroupsColumn);
    }

    if (!this.filterSubscription) {
      this.filterSubscription = this.dataFilterService.filterValueChanged$
        .pipe(
          untilDestroyed(this),
          tap(([column, value]) => {
            this.filterControls.columns.find(
              (x) => x.columnDef === column
            ).value = value;
            this.filterData();
          })
        )
        .subscribe();
    }

    setTimeout(() => {
      this.filterData();
    });
  }

  private recomputeDefaultColumnsWidths(): void {
    this.defaultColumns.sequence.definition.width =
      this.options?.sequenceConfig?.columnWidth ??
      this.defaultColumns.sequence.definition.width;

    this.defaultColumns.rowGroup.definition.width =
      this.options?.rowGroupConfig?.columnWidth ??
      this.defaultColumns.rowGroup.definition.width;

    this.defaultColumns.select.definition.width =
      this.options?.selectionConfig?.columnWidth ??
      this.defaultColumns.select.definition.width;

    this.defaultColumns.dragAndDrop.definition.width =
      this.options?.dragAndDropConfig?.columnWidth ??
      this.defaultColumns.dragAndDrop.definition.width;
  }

  private refreshFilters(): void {
    this.dataFilterService.refreshFilters$.next(
      Constants.DefaultValues.allValues
    );

    this.dataSource.filter = null;
  }

  private setAllowAdding(allowAdding: boolean): void {
    this.options.addConfig.allowAdding = allowAdding;
  }

  private filterData(): void {
    if (
      !!this.filterControls.columns.filter((x) =>
        String.isNullOrWhiteSpace(x.value?.toString())
      ).length
    ) {
      const filterValue = JSON.stringify(
        this.filterControls.columns.reduce((acc, val) => {
          acc[
            val.columnDef.split(`${this.filterControls.id}__filterColumn__`)[1]
          ] = val.value;
          return acc;
        }, {})
      );
      this.dataSource.filter = filterValue;

      if (
        this.dataSource.filteredData?.length &&
        this.options.colorConfig?.allowColorPicker
      ) {
        this.computeCellsColor();
      }

      if (this.data?.length && this.options.rowGroupConfig?.enableRowGrouping) {
        this.computeRowGroupsCellSpans();
      }

      this.cdr.detectChanges();
    }
  }

  private addRows(...values: T[]): T[] {
    if (!values?.length) {
      return [];
    }

    this.data = this.data ?? [];

    values.forEach((value) => {
      value.id = uuidv4();
      value.priority = this.data.length + 1;
      this.data.push(value);
    });

    this.changes.addValues(values);
    this.selection.sync(this.filterSelectableRows(this.data));

    this.refreshSorting();

    this.refreshDataSource();

    if (this.groupConfig) {
      this.updateGroupsOnNewData(values);
    }

    if (this.selection.getIsSelectAllActive()) {
      this.selectAll();
    }

    this.rendering$.next(true);
    this.zone.onMicrotaskEmpty
      .asObservable()
      .pipe(take(1))
      .subscribe(() => {
        this.rendering$.next(false);
      });

    return values;
  }

  private insertRows(dataList: T[], indexes: number[]): void {
    dataList.forEach((value, i) => {
      const valueIndex = indexes[i];

      const newData = {
        ...value,
        id: uuidv4(),
        priority: valueIndex + 1,
      };

      this.data.splice(valueIndex, 0, newData);

      this.changes.addValues([newData]);
    });

    this.selection.sync(this.filterSelectableRows(this.data));
    this.refreshSorting();
    this.refreshDataSource();

    this.updateRowsPriorities();

    if (this.selection.getIsSelectAllActive()) {
      this.selectAll();
    }
  }

  private updateRow(newData: T): boolean {
    const index = this.data.findIndex((row) => row.id === newData.id);
    if (index === -1) {
      throw new Error(
        `Cannot update row: No row found with id "${newData.id}".`
      );
    }

    const existing = this.data[index];

    // Detect changes
    const changedColumns = Object.keys(existing).filter(
      (key) => !objectEquals(existing[key], newData[key])
    );

    if (!changedColumns.length) {
      return false; // No visible changes, skip
    }

    // Apply the update
    const updated = { ...existing, ...newData };
    Object.assign(this.data[index], updated);

    // Track the change
    this.changes.write({
      value: updated,
      properties: changedColumns as (keyof T)[],
    });

    // Refresh UI row
    this.refreshSorting();
    this.refreshDataSource(true, updated.id);

    return true;
  }

  private recomputePriority(data: T): void {
    const groupId = data[this.groupConfig.groupBy];

    const groupData = this.data
      .filter(
        (x) => x[this.groupConfig.groupBy] === groupId && x.id !== data.id
      )
      .sort((a, b) => b.priority - a.priority);

    if (groupData?.length) {
      data.priority = groupData[0].priority + 1;
    } else {
      data.priority = 1;
    }

    this.collapsedGroups = this.collapsedGroups.filter((x) => x !== groupId);
  }

  private noGroupDrag(event: CdkDragDrop<(T | Group)[]>): void {
    if (
      !this.options?.dragAndDropConfig ||
      event.previousIndex === event.currentIndex
    ) {
      return;
    }

    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;

    const startIndex =
      previousIndex < currentIndex ? previousIndex : currentIndex;
    const endIndex =
      previousIndex < currentIndex ? currentIndex : previousIndex;

    const selectedRow = this.data[previousIndex];
    this.data.splice(previousIndex, 1);
    this.data.insert(selectedRow, currentIndex);

    const dragAndDropColumn = this.options?.dragAndDropConfig.columnDef;

    this.data.forEach((x, index) => {
      if (startIndex > index || index > endIndex) {
        return;
      }

      x[dragAndDropColumn] = (index + 1) as any;

      this.changes.write({
        value: x,
        properties: [dragAndDropColumn.toString()] as any,
      });
    });

    this.refreshDataSource();

    this.dragDisabled = true;
  }

  private groupDrag(event: CdkDragDrop<(T | Group)[]>): void {
    if (
      !this.options?.dragAndDropConfig ||
      event.previousIndex === event.currentIndex
    ) {
      return;
    }

    const previousIndex = this.dataSource.data.findIndex(
      (x) => x.id === event.item.data.id
    );
    const currentIndex = this.getDropIndex(
      previousIndex,
      event.currentIndex - event.previousIndex
    );

    const startIndex =
      previousIndex < currentIndex ? previousIndex : currentIndex;
    const endIndex =
      previousIndex < currentIndex ? currentIndex : previousIndex;

    const prevGroup = this.getFirstGroupFromIndex(previousIndex);
    const curGroup = this.getFirstGroupFromIndex(
      currentIndex,
      currentIndex > previousIndex
    );

    const refusedGroup = this.getRefusedGroup(
      previousIndex,
      prevGroup,
      curGroup
    );

    if (refusedGroup) {
      this.options.dragAndDropConfig.onGroupChangeRefused?.(
        this.dataSource.data[previousIndex] as T,
        refusedGroup
      );
      this.dragDisabled = true;
      return;
    }

    const curGroupIndex = this.dataSource.data.findIndex(
      (x: any) => this.isGroup(null, x) && x.value === curGroup
    );

    const selectedRow = this.dataSource.data[previousIndex];
    this.dataSource.data.splice(previousIndex, 1);
    this.dataSource.data.insert(selectedRow, currentIndex);

    const dragAndDropColumn = this.options?.dragAndDropConfig.columnDef;

    if (prevGroup !== curGroup) {
      const selItem = this.data.find((y) => y.id === selectedRow.id);
      selItem[this.groupByColumn] = curGroup;

      this.changes.write({
        value: selItem,
        properties: [`${this.groupByColumn}`] as any,
      });
    }

    this.dataSource.data.forEach((x: any, index) => {
      if (startIndex > index || index > endIndex || this.isGroup(null, x)) {
        return;
      }

      x[dragAndDropColumn] = (index + 1 - curGroupIndex) as any;
      this.data.find((y) => y.id === x.id)[dragAndDropColumn] =
        x[dragAndDropColumn];

      this.changes.write({
        value: x,
        properties: [dragAndDropColumn.toString()] as any,
      });
    });

    this.refreshDataSource();

    this.dragDisabled = true;
  }

  /**
   * @description The CDK orders rows by where they are on screen. A hidden row
   * (the header of a group without rows, a row of a collapsed group) has no
   * position, so the CDK sorts it first and the offset between the start and
   * drop indexes doesn't count it. The offset is counted over shown rows only.
   */
  private getDropIndex(previousIndex: number, offset: number): number {
    const shownRows = this.dataSource.data.filter((x: any) =>
      this.isGroup(null, x)
        ? this.data.some((y) => y[this.groupByColumn] === x.value)
        : !this.collapsedGroups.includes(x[this.groupByColumn])
    );
    const shownIndex = shownRows.indexOf(this.dataSource.data[previousIndex]);
    const target = shownRows[shownIndex + offset];

    return target
      ? this.dataSource.data.indexOf(target)
      : previousIndex + offset;
  }

  /**
   * @description Dropped onto a group header, a row moving down lands under it,
   * in that header's group, and a row moving up lands above it, in the group
   * before.
   */
  private getFirstGroupFromIndex(index: number, isMovingDown = false): any {
    let prevElement = this.dataSource.data[index] as any;
    if (prevElement?.level && index > 1 && !isMovingDown) {
      prevElement = this.dataSource.data[index - 1];
    }

    return prevElement?.level
      ? prevElement?.value
      : prevElement[this.groupByColumn];
  }

  private getRefusedGroup(
    previousIndex: number,
    prevGroup: any,
    curGroup: any
  ): Group | undefined {
    const allowGroupChange = this.options.dragAndDropConfig.allowGroupChange;

    if (!allowGroupChange || prevGroup === curGroup) {
      return undefined;
    }

    const row = this.dataSource.data[previousIndex] as T;
    const group = this.groups.find((x) => x.value === curGroup);

    return group && !allowGroupChange(row, group) ? group : undefined;
  }

  private computeGroups(): void {
    this.groupByColumn = this.groupConfig.groupBy;
    this.groups = this.buildGridGroups(this.groupConfig.groups);
    this.dataSource.data
      .filter((x) => !!(x as any).level)
      .forEach((x: any) => {
        x.name = this.groups.find((y) => y.value === x.value)?.name ?? x.name;
        x.isActive =
          this.groups.find((y) => y.value === x.value)?.isActive ?? x.isActive;
      });
    this.collapsedGroups = [];
  }

  private updateGroupsOnNewData(data: T[]): void {
    const updatedGroups = data.map((x: any) => x[this.groupByColumn]);
    this.collapsedGroups = this.collapsedGroups.filter(
      (x) => !updatedGroups.includes(x)
    );

    this.groupByColumn = this.groupByColumn.slice();
  }

  private deleteRows(...values: T[]): void {
    if (!values?.length) {
      return;
    }

    values.forEach((data) => {
      const rowIndex = this.data.indexOf(data);
      this.data.splice(rowIndex, 1);
      this.dataCellColors?.set(data.id, undefined);
    });

    this.changes.deleteValues(...values);
    this.selection.sync(this.filterSelectableRows(this.data));

    if (this.options?.dragAndDropConfig?.allowDragAndDrop) {
      this.updateRowsPriorities();
    }

    this.refreshDataSource();

    this.cdr.markForCheck();

    this.options?.deleteConfig?.onDeleted?.(...values);

    this.refreshFooter(this.displayedColumns);
  }

  /**
   * @description Looks inside the list of open dialogs
   * And checks if the last dialog element contains a reference to the current grid
   */
  private isCurrentGridOpenedInTopDialog(): boolean {
    const dialogRefs = this.dialog.openDialogs;
    if (
      (
        dialogRefs.length &&
        (dialogRefs[dialogRefs.length - 1]._containerInstance as any)
      )._elementRef.nativeElement.contains(this.element.nativeElement)
    ) {
      return true;
    }

    return false;
  }

  /**
   * @description Notifies about the column changes.
   * Subscription to changes is made through columns's onCellEdited property configured in each column
   */
  private notifyChanges(data: T, columnsDef: string[], oldValue?: any): void {
    this.columns
      .filter((x) => columnsDef.includes(x.columnDef) && x.onCellEdited)
      .forEach((x) => x.onCellEdited?.(data, oldValue));
  }

  private refreshRow(data: T, columnsDef: string[]): void {
    const shouldRefreshRow = this.columns.some(
      (x) => x?.recomputeRowOnChange && columnsDef.indexOf(x.columnDef) !== -1
    );

    if (shouldRefreshRow) {
      this.refreshService.refresh$.next(data.id);
    }
  }

  private refreshFooter(columnsDef: string[]): void {
    columnsDef?.forEach((prop) => {
      // timeout is needed to ensure that all related footer cells are being recalculated
      setTimeout(() => {
        this.shouldRecomputeFooter$.next({
          column: prop,
          timestamp: new Date(),
        });
      });
    });
  }

  private computeCellsColor(): void {
    const colorColumnDef = this.options.colorConfig.colorPickerColumnDef;
    const colorPalette = [
      Constants.DefaultValues.colorReset,
      ...this.options.colorConfig.colorPalette,
    ];

    setTimeout(() => {
      if (!this.dataCellColors) {
        this.dataCellColors = new Map<number | string, any>();
      }

      this.data.forEach((x) => {
        const cellColors = x[colorColumnDef];

        let updatedProperties = [];
        const prevCellColors = this.dataCellColors.get(x.id);

        if (prevCellColors) {
          const newProperties = Object.keys(cellColors).filter(
            (y) => cellColors[y] !== prevCellColors[y]
          );
          const removedProperties = Object.keys(prevCellColors).filter(
            (y) => !cellColors[y]
          );

          updatedProperties = [...newProperties, ...removedProperties];
        }

        Object.keys(x)
          .filter((prop) =>
            !updatedProperties.length
              ? cellColors.hasOwnProperty(prop) && cellColors[prop]
              : updatedProperties.includes(prop)
          )
          .forEach((p) => {
            this.colorService.colorCell$.next({
              dataId: x.id,
              columnDef: p,
              color: colorPalette.find((y) => y.id === cellColors[p]),
            });
          });

        this.dataCellColors.set(x.id, cellColors);
      });
    });
  }

  /**
   * @description Gets the current grid changes.
   * Rows considered empty are automatically excluded and will not be returned.
   * Unless explicity specified through options, the automatically generated ids of the added rows will be cleared.
   */
  private getChanges(): GridSync<T> {
    const changes = cloneDeep(this.changes.changed) as Changes<T>;

    const gridSyncChanges = {
      addedList: this.options?.addConfig?.keepAutogeneratedIds
        ? changes.added.filter((x) => !this.isRowEmpty(x))
        : changes.added
            .filter((x) => !this.isRowEmpty(x))
            .map((x) => ({ ...x, id: null })),
      updatedList: changes.updated,
      deletedIds: changes.deleted.map((x) => x.id),
      changedProperties: changes.propertiesChanges,
    };

    // This logic was added so that the changes model can be also addapted for specific use
    // cases from external components
    return this.options?.changesConfig?.changesAdapter
      ? this.options?.changesConfig?.changesAdapter(gridSyncChanges)
      : gridSyncChanges;
  }

  private getChangesWithUpdatedPriorities(): GridSync<T> {
    const changes = cloneDeep(this.changes.changed) as Changes<T>;

    if (this.options?.dragAndDropConfig?.allowDragAndDrop) {
      this.data
        .filter((row) => !this.isRowEmpty(row))
        .forEach((row, index) => {
          const newPriority = index + 1;

          if (row.priority !== newPriority) {
            row.priority = newPriority;
          }
        });

      changes.added
        .filter((x) => !this.isRowEmpty(x))
        .forEach(
          (x) =>
            (x.priority = this.data.find((row) => row.id === x.id).priority)
        );

      changes.updated
        .filter((x) => !this.isRowEmpty(x))
        .forEach(
          (x) =>
            (x.priority = this.data.find((row) => row.id === x.id).priority)
        );
    }

    return {
      addedList: this.options?.addConfig?.keepAutogeneratedIds
        ? changes.added.filter((x) => !this.isRowEmpty(x))
        : changes.added
            .filter((x) => !this.isRowEmpty(x))
            .map((x) => ({ ...x, id: null })),
      updatedList: changes.updated,
      deletedIds: changes.deleted.map((x) => x.id),
      changedProperties: changes.propertiesChanges,
    };
  }

  private isDirty(): boolean {
    return this.changes.dirty;
  }

  private getSelections(): T[] {
    return this.selection.selected;
  }

  /**
   * @description Merges the given changes into the changes model.
   * This is especially useful when editing grids in dialogs.
   *
   * NOTE: This will not alter the input data in any way.
   */
  private setPredefinedChanges(predefinedChanges: GridSync<T>): void {
    if (!predefinedChanges) {
      return;
    }

    const changes: Changes<T> = {
      added: predefinedChanges.addedList,
      updated: predefinedChanges.updatedList,
      deleted: predefinedChanges.deletedIds?.map((x) => ({ id: x } as any)),
      propertiesChanges: predefinedChanges.changedProperties,
    };

    this.changes.setChanges(changes);
    this.cdr.detectChanges();
  }

  private removeChanges(changes: string[]): void {
    if (!this.changes) {
      return;
    }

    this.changes.removeChanges(changes);
    this.cdr.detectChanges();
  }

  private isPropertyChanged(data: T, property: string): boolean {
    return this.changes?.isPropertyChanged(data, property as any);
  }

  /**
   * @deprecated Use your own saving mechanism for the sake of simplicity.
   * This will be removed from the grid.
   */
  private save(changes: GridSync<T>): Observable<T[]> {
    const save$ = this.options?.saveConfig
      .save(changes)
      .pipe(untilDestroyed(this), shareReplay());

    save$.subscribe({
      next: (result: T[]) => {
        this.onSaveSuccess(result);
      },
      error: (error: any) => {
        this.onSaveError(error);
      },
    });

    return save$;
  }

  /**
   * @deprecated Use your own saving mechanism for the sake of simplicity.
   * This will be removed from the grid.
   */
  private onSaveSuccess(result: T[]): void {
    this.reset();

    this.data = result;

    this.refreshDataSource(true);
  }

  /**
   * @deprecated Use your own saving mechanism for the sake of simplicity.
   * This will be removed from the grid.
   */
  private onSaveError(error: any): void {
    if (this.options?.saveConfig?.onError) {
      return this.options?.saveConfig.onError(error);
    }

    this.dialog.open(ConfirmationDialogComponent, {
      ...DefaultDialogConfig,
      data: {
        title: this.options?.saveConfig?.errorTitle ?? 'Save error.',
        message:
          this.options?.saveConfig?.errorMessage ??
          'An error has occurred while saving your data, please try again.',
        confirmButtonLabel: this.options?.labeling?.okButton ?? 'Ok',
        hideCancelButton: true,
      },
    });
  }

  private isRowEmpty(data: T): boolean {
    const added = this.changes.isValueAdded(data);

    if (this.options?.rowConfig?.isEmpty) {
      return added && this.options?.rowConfig.isEmpty(data);
    }

    // If rowEmpty function was not provided in grid options
    // the `value` field from addConfig will be considered the default
    const defaultValue = {
      ...this.rowDefaultValue,
      id: data.id,
    };
    return added && objectEquals(data, defaultValue);
  }

  /**
   * @description Clears up the changes and the selection models.
   */
  private reset(): void {
    this.changes.clear();
    this.selection.clear();

    this.cdr.detectChanges();
  }

  /**
   * @description Filters the given rows and excludes the rows that can not be selected
   */
  private filterSelectableRows(rows: T[]): T[] {
    return (rows ?? []).filter(
      (x) =>
        this.options?.selectionConfig?.allowSelection &&
        anyFnExec(this.options?.rowConfig?.allowSelection, x)
    );
  }

  private selectAll(): void {
    this.selection.selectAll();
  }

  /**
   * @description Gets the html row element attached to the given data
   */
  private getRowElement(row: T): HTMLElement {
    const rows = this.matTable._getRenderedRows(this.matTable._rowOutlet) ?? [];

    return rows.find((x) => x.id === row?.id);
  }

  /**
   * @description Scrolls to the last row of the table
   */
  private scrollToLastRow(): void {
    if (this.data.length) {
      this.getRowElement(this.data[this.data.length - 1])?.scrollIntoView();
    }
  }

  /**
   * @description Scrolls to the row attached to the given data
   */
  private scrollTo(row: T): void {
    this.getRowElement(row)?.scrollIntoView({ block: 'end' });
  }

  /**
   * @description Focuses the the first focusable cell of the row attached to the given data
   */
  private focusRow(row: T, startEdit: boolean): void {
    const rowElement = this.getRowElement(row);

    if (startEdit) {
      const cells = Array.from(rowElement.getElementsByTagName('mat-cell'));
      const cell = cells.find((x) => x.getAttribute('tabIndex') === '0');

      if (cell) {
        this.focusMonitor.focusVia(
          cell as HTMLElement,
          startEdit ? 'mouse' : null
        );
      }
    } else {
      // TODO(Grid) find a way to focus the row
      rowElement.focus();
    }
  }

  /**
   * @description Open edit programmatically
   */
  private openEdit(
    columnDef: string,
    id: number | string,
    additionalData?: any
  ): void {
    this.editService.openEdit(columnDef, id, additionalData);
  }

  private refreshDataSource(
    repaintRows?: boolean,
    rowId?: number | string
  ): void {
    // TODO(Grid)! do we need this => check when sorting on on dpr activities is implemented
    this.dataSource.data = !this.groupConfig
      ? [...(this.data ?? [])]
      : this.getDataPerGroup();

    if (repaintRows) {
      setTimeout(() => {
        if (rowId) {
          this.repaintRow(rowId);
        } else {
          this.repaintRows();
        }
      });
    }
  }

  private getDataPerGroup(): (T | Group)[] {
    this.groups = this.buildGridGroups(this.groupConfig.groups);
    this.groupByColumn = this.options.groupConfig.groupBy;

    let result: (T | Group)[] = [];

    const dragAndDropColumn = this.options?.dragAndDropConfig.columnDef;

    this.groups.forEach((x) => {
      let i = 1;

      const gridData = this.data.filter(
        (y) => y[this.groupByColumn] === x.value
      );

      gridData.sort(
        (y: any, z: any) => y[dragAndDropColumn] - z[dragAndDropColumn]
      );

      gridData.forEach((y: T) => {
        y.customIndex = i++;

        y[dragAndDropColumn] = y.customIndex as any;
      });

      result = [...result, x, ...gridData];
    });

    return result;
  }

  private buildGridGroups(groups: Group[]): Group[] {
    return [
      ...groups.map((x) => ({
        name: x.name,
        value: x.value,
        level: 1,
        isActive: x.isActive,
        inactiveLabel: x.inactiveLabel,
        id: uuidv4(),
      })),
      {
        name: this.options?.labeling?.unassignedGroupLabel ?? 'Unassigned',
        value: Constants.DefaultValues.unassignedGroupId,
        inactiveLabel: '',
        level: 1,
        id: uuidv4(),
      },
    ];
  }

  private computeRowGroupsCellSpans(): void {
    const data = this.dataSource.filteredData as T[];

    const rowGroupColumn = this.options.rowGroupConfig.columnDef;
    this.rowGroupsCellSpans = Array(data.length).fill(0);

    for (let i = 0; i < data.length; ) {
      const currentValue = data[i][rowGroupColumn];
      let count = 1;

      for (let j = i + 1; j < data.length; j++) {
        if (currentValue !== data[j][rowGroupColumn]) {
          break;
        }

        count++;
      }

      this.rowGroupsCellSpans[i] = count;
      i += count;
    }
  }

  private repaintRows(): void {
    this.refreshSelectableRows();
    this.refreshService.refresh$.next('ALL_ROWS');
  }

  private repaintRow(rowId: number | string): void {
    this.refreshService.refresh$.next(rowId);
  }

  private refreshSelectableRows(): void {
    this.selection.sync(this.filterSelectableRows(this.data));
    this.rowsRevision++;

    // The grid is OnPush and this runs from outside its own event handlers, so
    // the new revision would not render until something else marked the view.
    this.cdr.markForCheck();
  }

  private updateRowsPriorities(): void {
    this.data.forEach((row, index) => {
      const newPriority = index + 1;

      if (row.priority !== newPriority) {
        row.priority = newPriority;

        this.changes.write({ value: row, properties: ['priority'] });
      }
    });
  }

  private refreshSorting(): void {
    if (!this.options?.sortConfig) {
      return;
    }

    this.applySortingDefinition();

    this.refreshDataSource();
  }

  private sortingFn(...columns: SortDescriptor<T>[]): (a: T, b: T) => number {
    return (a: T, b: T) => {
      for (const column of columns) {
        const accessor =
          column.direction === GridSortDirection.Ascending ? 1 : -1;
        const prop = column.columnDef;

        const firstValue = column.sortValueAccessor
          ? column?.sortValueAccessor(prop, a)
          : a[prop];
        const secondValue = column.sortValueAccessor
          ? column?.sortValueAccessor(prop, b)
          : b[prop];

        // skip values that are equal
        if (firstValue === secondValue) {
          continue;
        }

        // sort nulls after anything else
        if (firstValue == null) {
          return accessor;
        }
        if (secondValue == null) {
          return -accessor;
        }

        if (typeof firstValue === 'string' && typeof secondValue === 'string') {
          return firstValue.localeCompare(secondValue, undefined, {
            sensitivity: 'accent',
          }) > 0
            ? accessor
            : -accessor;
        }

        return firstValue > secondValue ? accessor : -accessor;
      }

      return 0;
    };
  }

  /**
   * @description Applies the given sorting definition to the grid and repaints it.
   */
  private setSortingDefinition(definition: SortDescriptor<T>[]): void {
    this.sortingDefinition = definition;

    this.applySortingDefinition();
  }

  private applySortingDefinition(): void {
    // TODO(Grid) if list is empty reset sorting ???
    if (this.sortingDefinition?.length) {
      this.data?.sort(this.sortingFn(...this.sortingDefinition));
      // this is needed to make sure the row index is updated immediately after save
      setTimeout(() => {
        this.cdr.detectChanges();
      });
    }

    this.refreshDataSource();
  }

  /**
   * @description Aggregates the enabled, default columns with the provided columns
   */
  private computeDisplayedColumns(): void {
    this.displayedColumns = [
      ...Object.entries(this.defaultColumns)
        .filter(([_, value]) => value.enabled())
        .map(([_, value]) => value.definition.columnDef),
      ...(this.columns?.map((x) => x.columnDef) || []),
    ];
    if (this.filterControls) {
      this.filteredColumns = this.displayedColumns.map((x) => {
        if (this.defaultColumns.rowGroup.definition.columnDef === x) {
          return `${
            this.filterControls.id
          }__filterColumn__${this.options.rowGroupConfig.columnDef.toString()}`;
        }
        return `${this.filterControls.id}__filterColumn__${x}`;
      });
    }
  }

  private get filterRowGroupsColumn(): FilterColumn {
    return {
      columnDef: `${
        this.filterControls.id
      }__filterColumn__${this.options.rowGroupConfig.columnDef.toString()}`,
      edit: {
        type: FilterSelectEditComponent,
        config: {
          alignment: GridCellContentAlignment.Left,
          autoOpen: false,
          autofocus: false,
          options: this.options.rowGroupConfig.groups,
          includeNoneOption: false,
        },
      },
    };
  }

  private get rowDefaultValue(): any {
    if (!this.options?.addConfig?.value) {
      return {};
    }

    return typeof this.options?.addConfig.value === 'function'
      ? this.options?.addConfig.value()
      : cloneDeep(this.options?.addConfig?.value);
  }

  private get groupConfig(): GroupConfig {
    return this.options.groupConfig;
  }

  private filterPredicate(data: T | Group, filterStringValue: string): boolean {
    if (!filterStringValue) {
      return true;
    }
    const filterValue = JSON.parse(filterStringValue);

    // loop for every property of filter values
    for (const prop in filterValue) {
      // if the property if null or was set to empty string, skip the check
      if (
        !filterValue[prop] ||
        String.isNullOrWhiteSpace(filterValue[prop].toString())
      ) {
        continue;
      }

      // if data of property is empty, return false
      if (data[prop] == null) {
        return false;
      }

      const computeFilterDataValueFn = this.filterControls.columns.find(
        (x) =>
          x.columnDef === `${this.filterControls.id}__filterColumn__${prop}`
      )?.computeFilterDataValueFn;

      const computedData = computeFilterDataValueFn
        ? computeFilterDataValueFn(data[prop])
        : data[prop];

      // if the prop value is an array, check if any element matches the data values
      if (
        Array.isArray(filterValue[prop]) &&
        !filterValue[prop]
          .map((x) => x.toString().toLowerCase())
          .includes(computedData.toString().toLowerCase())
      ) {
        return false;
      }

      // if the prop value is not an array, check if the filter value is a substring of the data value
      if (
        !Array.isArray(filterValue[prop]) &&
        !computedData
          .toString()
          .toLowerCase()
          .includes(filterValue[prop].toString().toLowerCase())
      ) {
        return false;
      }
    }

    return true;
  }

  //#endregion
}
